/**
 * Copyright © 2026 Adnuntius AS.
 */
import {firstValueFrom, Subscription} from 'rxjs';
import {Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild, signal} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {FormsModule} from '@angular/forms';
import {TranslateModule} from '@ngx-translate/core';
import {TabsModule} from 'ngx-bootstrap/tabs';
import * as _ from 'lodash';
import * as moment from 'moment';
import 'moment-timezone';
import * as Highcharts from 'highcharts';

import template from './network-cdn-traffic.component.html';

import {Downloader} from '../../../components/util/downloader';
import {StatsApiService} from '../../common/controller/stats-api.service';
import {CHART_COLORS} from '../../common/options/chart-colors';
import {ADN_CHART_COUNT_TYPES, ADN_CHART_Y_AXIS_OPTIONS} from '../../common/options/chart-options';
import {AdnLogService} from '../../../components/util/adn-log.service';
import {AppLocaleService} from '../../common/locale/app-locale.service';
import {getIntlLocaleData} from '../../common/locale/intl-locale-data';
import {applyHighchartsLocale} from '../../workspace/widget/workspace-widget-chart-commons';
import {UserProfileApiService} from '../user-profile/user-profile-api.service';
import {ChartOptionsUtil} from '../../common/options/chart-options-util.service';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {APP_SHARED} from '../../common/app-shared';
import {CHARTS} from '../../common/directives/charts';
import {deferSignalStateWrite} from '../../common/util/template-state';

interface NetworkCdnTrafficViewState {
  haveStats: boolean;
  error: boolean;
  isPending: boolean;
  showTeras: boolean;
  totalMegs: number;
  totalGigs: number;
  totalTeras: number;
}

@Component({
  standalone: true,
  imports: [...APP_SHARED, FormsModule, TranslateModule, TabsModule, ...CHARTS],
  selector: 'adn-network-cdn-traffic',
  template
})
export class NetworkCdnTrafficComponent implements OnInit, OnDestroy {
  @ViewChild('chartContainer')
  set chartContainer(chartContainer: ElementRef<HTMLElement>) {
    this.chartContainerRef = chartContainer;
    this.renderChart();
  }

  dateOption: any;
  startDate: any;
  endDate: any;
  countType = ADN_CHART_COUNT_TYPES.singular;
  yAxisOption = ADN_CHART_Y_AXIS_OPTIONS.multiple;
  readonly viewState = signal<NetworkCdnTrafficViewState>({
    haveStats: false,
    error: false,
    isPending: true,
    showTeras: true,
    totalMegs: 0,
    totalGigs: 0,
    totalTeras: 0
  });

  private chart: Highcharts.Chart | null = null;
  private chartContainerRef: ElementRef<HTMLElement> | null = null;
  private chartOptions: any = null;
  private series: any[] = [];
  private chartPerfProfileKey = '';
  private localUser: any;
  private profile: any;
  private statsSubscription?: Subscription;
  private loadRevision = 0;

  constructor(
    private readonly statsApi: StatsApiService,
    private readonly appLocale: AppLocaleService,
    private readonly translate: TranslateService,
    private readonly adnLog: AdnLogService,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    @Inject(ChartOptionsUtil) private readonly ChartOptionsUtil: any,
    @Inject(UserProfileApiService) private readonly UserProfile: UserProfileApiService,
    @Inject(LocalUserProfile) private readonly localUserProfile: any,
  ) {
    const timezone = this.LocalNetworkProfile.get('timeZone');
    const networkTimezoneOffset = moment.tz(timezone).utcOffset();

    applyHighchartsLocale(getIntlLocaleData(this.appLocale.getLocaleId()), networkTimezoneOffset);
  }

  ngOnInit(): void {
    const chartOptions = this.ChartOptionsUtil.getOptions('CDN_STATS');
    this.chartPerfProfileKey = chartOptions.perfChartOptions;
    this.localUser = this.localUserProfile.get();
    this.profile = this.localUser.profile;
    const profileStartDate = _.get(this.profile[this.chartPerfProfileKey], 'startDate');
    const profileEndDate = _.get(this.profile[this.chartPerfProfileKey], 'endDate');
    const profileCountType = ADN_CHART_COUNT_TYPES[_.get(this.profile[this.chartPerfProfileKey], 'countTypeId')];

    this.dateOption = this.ChartOptionsUtil.getDateOptions()[_.get(this.profile[this.chartPerfProfileKey], 'dateOptionId')];
    if (!this.dateOption && (!profileStartDate || !profileEndDate)) {
      this.dateOption = this.ChartOptionsUtil.getDateOptions().last30Days;
    }

    this.startDate = this.dateOption ? this.dateOption.startDate() : moment(profileStartDate);
    this.endDate = this.dateOption ? this.dateOption.endDate() : moment(profileEndDate);
    this.countType = profileCountType || ADN_CHART_COUNT_TYPES.singular;

    this.loadStats();
  }

  ngOnDestroy(): void {
    this.loadRevision++;
    this.statsSubscription?.unsubscribe();
    this.destroyChart();
  }

  changeCallback(
    dateOptionId: string,
    startDate: any,
    endDate: any,
    countTypeId: string,
    statTypeId?: string,
    yAxisOptionId?: string,
    selFieldIds?: any,
    selFieldId?: any
  ): void {
    void statTypeId;
    void selFieldIds;
    void selFieldId;

    this.dateOption = this.ChartOptionsUtil.getDateOptions()[dateOptionId];
    this.startDate = moment(startDate);
    this.endDate = moment(endDate);
    this.countType = ADN_CHART_COUNT_TYPES[countTypeId];
    this.yAxisOption = ADN_CHART_Y_AXIS_OPTIONS[yAxisOptionId || ADN_CHART_Y_AXIS_OPTIONS.multiple.id];
    this.loadStats();

    this.profile[this.chartPerfProfileKey] = {
      dateOptionId: _.get(this.dateOption, 'id'),
      startDate: this.startDate.toDate(),
      endDate: this.endDate.toDate(),
      yAxisOptionId: this.yAxisOption.id,
      countTypeId: this.countType.id
    };

    firstValueFrom(this.UserProfile.saveProfile({
      id: this.localUser.userId,
      profile: _.pick(this.profile, this.chartPerfProfileKey)
    })).then((apiUser: any) => {
      this.localUser = this.localUserProfile.safeSaveProfile(this.chartPerfProfileKey, apiUser.profile[this.chartPerfProfileKey]);
      this.profile = this.localUser.profile;
    });
  }

  save(allFields?: boolean, format = 'xlsx', totalsOnly?: boolean): void {
    void allFields;
    void totalsOnly;

    const chartOptions = this.ChartOptionsUtil.getOptions('CDN_STATS');
    const allDataFields = _.clone(chartOptions.fields);
    const headingFields = _.map(allDataFields, 'name');
    const fields = _.map(allDataFields, 'id');
    const rows: any[] = [];
    const refData: any[] = _.cloneDeep(this.series[0]?.data || []);

    for (let i = 0; i < refData.length; i++) {
      const row: any = {
        date: moment(refData[i].x).toString()
      };

      _.forEach(this.series, (series: any) => {
        row[series.id] = series.data[i].y;
      });

      rows.push(row);
    }

    headingFields.unshift('Date');
    fields.unshift('date');

    const date = this.dateOption ? this.translate.instant('chart.dateOption.' + this.dateOption.id) : 'custom dates';
    const extension = format === 'xlsx' ? 'xlsx' : 'xls';
    const defaultName = ('cdn-stats-' + date + '.' + extension).replace(/ /g, '-');
    new Downloader().saveXls2(defaultName, headingFields, fields, rows, []);
  }

  private loadStats(): void {
    const loadRevision = ++this.loadRevision;
    const chunkSize = this.ChartOptionsUtil.getChunkSize(this.startDate, this.endDate);
    const statsParams = {
      startDate: this.startDate,
      endDate: this.endDate,
      groupBy: chunkSize.key
    };

    this.deferLoadCommit(loadRevision, () => {
      this.viewState.update((state) => ({
        ...state,
        error: false,
        isPending: true
      }));
    });

    this.statsSubscription?.unsubscribe();
    this.statsSubscription = this.statsApi.getCdn(statsParams).subscribe({next: (stats) => {
      const bytes = _.get(stats, ['totals', 'bytes']);
      const gigs = this.toUnits(bytes);
      const showTeras = gigs >= 15;
      const divisor = showTeras ? 3 : 2;

      const runningSeries = {
        type: 'line' as const,
        id: 'traffic',
        name: 'Traffic',
        color: CHART_COLORS[0],
        yAxis: 0,
        data: _.map(stats.chunkRange, (statsDate: any) => {
          const chunk = _.find(stats.chunks, {chunkStart: statsDate});
          const value = this.toUnits(_.get(chunk, 'bytes'), divisor);
          return {
            x: moment(statsDate).valueOf(),
            y: value
          };
        })
      };

      if (this.countType === ADN_CHART_COUNT_TYPES.cumulative) {
        let runningTotal = 0;
        runningSeries.data = _.map(runningSeries.data, (point: any) => {
          runningTotal += point.y;
          return {
            x: point.x,
            y: runningTotal
          };
        });
      }

      const series = [runningSeries];
      const chartOptions = this.createChartOptions(series, showTeras);
      this.deferLoadCommit(loadRevision, () => {
        this.series = series;
        this.chartOptions = chartOptions;
        this.viewState.set({
          haveStats: true,
          error: false,
          isPending: false,
          showTeras,
          totalMegs: this.toUnits(bytes, 2),
          totalGigs: gigs,
          totalTeras: this.toUnits(bytes, 4)
        });
        this.renderChart();
      });
    }, error: (data) => {
      this.deferLoadCommit(loadRevision, () => {
        this.adnLog.warn(data);
        this.viewState.update((state) => ({
          ...state,
          error: data.status !== -1,
          isPending: false
        }));
      });
    }});
  }

  private deferLoadCommit(loadRevision: number, apply: () => void): void {
    deferSignalStateWrite(() => {
      if (loadRevision === this.loadRevision) {
        apply();
      }
    });
  }

  private createChartOptions(series: any[], showTeras: boolean): any {
    return {
      chart: {
        type: 'line',
        zoomType: 'x',
        showAxes: true,
        height: 300
      },
      plotOptions: {
        series: {
          marker: {
            enabled: false
          },
          turboThreshold: 2000
        }
      },
      tooltip: {
        shared: true
      },
      xAxis: {
        type: 'datetime',
        title: {
          enabled: false,
          text: 'Date and Time'
        }
      },
      yAxis: {
        title: {text: null},
        min: 0,
        opposite: false,
        allowDecimals: true,
        id: '0',
        labels: {
          format: showTeras ? '{value} GB' : '{value} MB',
          style: {color: String(CHART_COLORS[0])}
        }
      },
      series,
      title: {
        text: null
      },
      credits: {
        enabled: false
      },
      accessibility: {
        enabled: false
      },
      exporting: {
        enabled: true
      }
    };
  }

  private renderChart(): void {
    if (!this.chartContainerRef?.nativeElement || !this.chartOptions) {
      return;
    }

    this.destroyChart();
    this.chart = Highcharts.chart(this.chartContainerRef.nativeElement, this.chartOptions);
  }

  private destroyChart(): void {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }

  private toUnits(num: any, divisors = 3): number {
    const divisor = Math.pow(1024, divisors);
    return num ? num / divisor : 0;
  }
}
